// Copyright (c) 2019-2025 Cigent Technology Inc. All Rights Reserved.
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'src/services/user-service';
import { IUser } from 'src/types/user';
import { NotificationService } from 'src/services/notification-service';
import { Countries } from 'src/types/countries';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

const resolvedPromise = Promise.resolve(null);

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserDialogComponent implements OnInit {
  editUserForm: UntypedFormGroup;
  user: IUser;
  submitted = false;
  business_edition: number;
  organizations: number;
  @ViewChild('emailInput') emailInput: ElementRef;
  @ViewChild('usernameInput') usernameInput: ElementRef;

  fHeader: string;
  fUser: string;
  fContact: string;
  fEmail: string;
  fEReq: string;
  fEInvalid: string;
  fFName: string;
  fLName: string;
  fZip: string;
  fSettings: string;
  fPremium: string;
  fCancel: string;
  fSave: string;
  fErrorNameExists: string;
  fErrorEmailExists: string;
  fErrorInvalidEmail: string;

  constructor(public dialogRef: MatDialogRef<EditUserDialogComponent>,
              private userSvc: UserService,
              private formBuilder: UntypedFormBuilder,
              private translate: TranslateService,
              private notification: NotificationService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.translate.get(['CANCEL', 'SAVE', 'FIRST', 'LAST', 'EU_USER', 'EU_HEADER', 'EU_CONTACT', 'EU_EMAIL', 'EU_EMAIL_REQ',
      'EU_EMAIL_INVALID', 'EU_SETTINGS', 'EU_PREMIUM', 'EU_ZIP',
      'USER_ERROR_NAME_EXISTS', 'USER_ERROR_EMAIL_EXISTS', 'USER_ERROR_INVALID_EMAIL']).subscribe(vals => {
      this.fHeader = vals['EU_HEADER'];
      this.fUser = vals['EU_USER'];
      this.fContact = vals['EU_CONTACT'];
      this.fEmail = vals['EU_EMAIL'];
      this.fEReq = vals['EU_EMAIL_REQ'];
      this.fEInvalid = vals['EU_EMAIL_INVALID'];
      this.fZip = vals['EU_ZIP'];
      this.fFName = vals['FIRST'];
      this.fLName = vals['LAST'];
      this.fSettings = vals['EU_SETTINGS'];
      this.fPremium = vals['EU_PREMIUM'];
      this.fSave = vals['SAVE'];
      this.fCancel = vals['CANCEL'];
      this.fErrorNameExists = vals['USER_ERROR_NAME_EXISTS'];
      this.fErrorEmailExists = vals['USER_ERROR_EMAIL_EXISTS'];
      this.fErrorInvalidEmail = vals['USER_ERROR_INVALID_EMAIL'];
    });
  }

  ngOnInit(): void {
    this.editUserForm = this.formBuilder.group({
      username: [{value: ''}, [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)]],
      fname: ['', [Validators.nullValidator, Validators.maxLength(255)]],
      lname: ['', [Validators.nullValidator, Validators.maxLength(255)]],
      address1: ['', Validators.nullValidator],
      address2: ['', Validators.nullValidator],
      city: ['', Validators.nullValidator],
      state: ['', Validators.nullValidator],
      zip: ['', Validators.nullValidator],
      country: ['', Validators.nullValidator],
      organizations: [0, Validators.nullValidator],
      business_edition: [1, Validators.nullValidator],
    });
    const self = this;
    this.user = this.data.user;
    resolvedPromise.then(() => {
      self.editUserForm.controls['username'].setValue(self.user.username);
      self.editUserForm.controls['email'].setValue(self.user.email);
      self.editUserForm.controls['fname'].setValue(self.user.fname);
      self.editUserForm.controls['lname'].setValue(self.user.lname);
      self.editUserForm.controls['zip'].setValue(self.user.billing_zip_code);
      self.editUserForm.controls['country'].setValue(Countries.find(co => co.code === self.user.billing_country_code));
      self.editUserForm.controls['business_edition'].setValue(self.user.business_edition);
      self.business_edition = self.user.business_edition;
      self.organizations = self.user.organizations;
    });
  }


  // convenience getter for easy access to form fields
  get f() {
    return this.editUserForm.controls;
  }

  cancel() {
    this.dialogRef.close();
  }

  editUser() {
    this.submitted = true;

    if (this.editUserForm.invalid) {
      return;
    }

    this.translate.get(['UPDATE_USER', 'UPDATE_COMPLETE']).subscribe(
        (vals) => {
          this.notification.showUpdate(vals['UPDATE_USER'] + '...');
          const userInfo: IUser = {
            id: this.user.id,
            username: this.editUserForm.value.username,
            email: this.editUserForm.value.email,
            fname: this.editUserForm.value.fname,
            lname: this.editUserForm.value.lname,
            billing_zip_code: this.editUserForm.value.zip,
            billing_country_code: this.editUserForm.value.country.code,
            second_factor: null,
            business_edition: this.editUserForm.value.business_edition,
          };

          this.userSvc.updateUser(userInfo)
              .subscribe(
                () => {
                    this.dialogRef.close();
                  },
                () => {
                  this.notification.showError(vals['USER_UPDATE_FAIL']);
                },
                () => {
                  this.notification.showSuccess(vals['UPDATE_COMPLETE']);
                }
              );
        }
    );
  }

  business_edition_changed(e) {
    if (e.checked) {
      this.editUserForm.value.business_edition = 1;
    } else {
      this.editUserForm.value.business_edition = 0;
    }
  }

  onUsernameBlur() {
    this.userSvc.checkUsernameExists(this.data.user.id, this.editUserForm.value.username)
        .subscribe({
          next: (result) => {
            if (result.exists) {
              this.notification.showError(this.fErrorNameExists);
              this.usernameInput.nativeElement.focus();
              this.usernameInput.nativeElement.select();
            }
          }
        });
  }

  onEmailBlur() {
    if (this.editUserForm.controls.email.errors) {
      return;
    }

    this.userSvc.checkUserEmailExistsForUser(this.data.user.id, this.editUserForm.value.email)
        .subscribe({
            next: (e) => {
              if (e.exists && e.verified) {
                this.editUserForm.controls.email.setErrors({'exists': true});
              }
            },
            error: () => this.editUserForm.controls.email.setErrors({'pattern': true})
        });
  }
}
